/**
 * Fixture endpoint discovery.
 *
 * Derives API path → fixture filename(s) from the fixture directory structure.
 * Convention: public/fixtures/shipments/get-active-shipments.json
 *           → API path /apollo/shipments/get-active-shipments
 *
 * Variants (cycling): get-shipment-by-id1.json, get-shipment-by-id2.json
 * are grouped under a single API path /apollo/shipments/get-shipment-by-id.
 *
 * Aliases: when two API paths share the same fixture data, add an entry
 * to FIXTURE_ALIASES below.
 */

export interface FixtureEndpoint {
  path: string;
  fixtures: string[];
}

/** API paths that share a fixture with another path */
const FIXTURE_ALIASES: Record<string, string> = {
  // Both endpoints return the same label group data
  "/apollo/company-labels/get-company-labels-with-label-group":
    "company-labels/get-all-label-groups-with-labels.json",
  // Link company dialog fetches available companies on open
  "/apollo/companies/get-available-companies-for-linking":
    "companies/dashboard-users-table-data.json",
  // Detail endpoint returns same shape as list (array of ApiKeyWithCompanyAndUsageSummaries)
  "/apollo/api-keys/get-api-key-with-company-usage-data":
    "api-keys/list-all-api-keys.json",
  // Amy endpoint lives under amy/ API but fixture is in companies/
  "/apollo/amy/upcoming-amy-shipment-tasks":
    "companies/upcoming-amy-shipment-tasks.json",
  // Sales endpoints live under companies/ API but fixtures are in sales/
  "/apollo/companies/list-sales-calls": "sales/sales-calls.json",
  "/apollo/companies/list-sales-contacts": "sales/sales-contacts.json",
};

/**
 * Build fixture endpoints from a list of fixture filenames.
 *
 * In tests: filenames come from scanning public/fixtures/ on disk.
 * In browser: filenames come from the static list below.
 */
export function buildFixtureEndpoints(
  fixtureFilenames: string[],
): FixtureEndpoint[] {
  // Group variants: "shipments/get-shipment-by-id1.json" and
  // "shipments/get-shipment-by-id2.json" → path "/apollo/shipments/get-shipment-by-id"
  const pathMap = new Map<string, string[]>();

  for (const filename of fixtureFilenames) {
    // Strip .json extension, then strip trailing digits for variant grouping
    const withoutExt = filename.replace(/\.json$/, "");
    const baseName = withoutExt.replace(/\d+$/, "");
    const apiPath = `/apollo/${baseName}`;

    const existing = pathMap.get(apiPath) ?? [];
    existing.push(filename);
    pathMap.set(apiPath, existing);
  }

  const endpoints: FixtureEndpoint[] = [];
  for (const [path, fixtures] of pathMap) {
    // Sort variants numerically (id1, id2, id3...)
    fixtures.sort();
    endpoints.push({ path, fixtures });
  }

  // Add aliases
  for (const [aliasPath, fixtureFile] of Object.entries(FIXTURE_ALIASES)) {
    endpoints.push({ path: aliasPath, fixtures: [fixtureFile] });
  }

  return endpoints;
}

/**
 * The fixture files the browser can serve (public assets can't be globbed).
 * Each is fetched the first time a story or mocked API call asks for it.
 * `endpoints.test.tsx` fails when a `getFixture()` call names a group
 * missing from this list, or when a listed file is missing from disk.
 *
 * TODO: Replace with dynamic filesystem scan in worker.tsx
 * so adding a fixture file is the only step needed.
 */
export const fixtureEndpoints: FixtureEndpoint[] = buildFixtureEndpoints([
  "shipments/get-active-shipments.json",
  "shipments/get-shipment-by-id1.json",
  "shipments/get-shipment-by-id2.json",
  "shipments/get-shipment-by-id3.json",
  "shipments/get-shipment-by-id4.json",
  "shipments/get-shipment-by-id5.json",
  "shipments/get-shipment-by-id6.json",
  "companies/view-company-data1.json",
  "companies/view-company-data2.json",
  "companies/list-companies.json",
  "companies/dashboard-users-table-data.json",
  "companies/list-invitations-by-company.json",
  "companies/get-company-branches.json",
  "companies/get-saved-commodities-for-company.json",
  "shipments/get-claims.json",
  "shipments/shipments-with-operational-tasks.json",
  "shipments/shipments-with-manual-quoting.json",
  "queue/list-queue-tasks.json",
  "queue/queue-status.json",
  "shipments/recent-manual-quotes.json",
  "experiments/list-experiments.json",
  "api-keys/list-all-api-keys.json",
  "carrier-pro-block/get-all-carrier-pro-block.json",
  "fuel-surcharge-values/get-all-fuel-surcharge-values.json",
  "fuel-surcharge-values/get-all-fca-fuel-surcharge-values-history.json",
  "carrier-restrictions/get-all-carrier-restrictions.json",
  "carrier-restrictions/history-for-id.json",
  "quotes/visibility-history.json",
  "profit-loss/generate-profit-loss-report.json",
  "company-labels/get-all-label-groups-with-labels.json",
  "shipments/get-shipment-contacts-tab-by-id.json",
  "shipments/get-shipment-payment-method-info-by-id.json",
  "shipments/sent-edi-204s.json",
  "technical/get-all-claude-prompts.json",
  "customer-specific-pricing/all.json",
  "shipment-filters/generate-shipment-filters.json",
  "shipment-filters/get-companies.json",
  "shipment-filters/search.json",
  "shipment-filters/header-stats.json",
  "shipment-filters/map-points.json",
  "shipment-filters/calendar.json",
  "shipment-filters/statistics.json",
  "experiments/get-experiment-detail.json",
  "experiments/get-experiment-runs.json",
  "experiments/get-experiment-shipment-reports.json",
  "experiments/get-experiment-run-snapshots.json",
  "carriers/get-carrier-info.json",
  // ViewCarrierPage loads properties alongside info and no longer swallows a
  // failed request, so the browser worker must answer this one too.
  "carriers/get-carrier-properties.json",
  // Real endpoint is the flat /apollo/carrier-documents, so the fixture lives at
  // the fixtures root (not carriers/) to map correctly. The browser worker reads
  // this static list, so the page-level documents fetch (tab badge count) needs
  // it here — otherwise the request falls through to the network in Storybook.
  "carrier-documents.json",
  "carrier-invoice-audit/open-invoice-audits.json",
  "carrier-invoice-audit/get-all-invoice-carrier-notes.json",
  "carrier-invoice-audit/get-all-invoice-problem-notes.json",
  "carrier-invoice/get-carrier-request-audit-for-selected-quote.json",
  "carrier-invoice/get-payment-properties.json",
  "documents/download-document.json",
  "shipments/fedex-address-validation-information.json",
  "shipments/view-pro-number-history.json",
  "shipments/view-carrier-pickup-number-history.json",
  "starred-objects/get-starred-objects-for-user.json",
  "sales/sales-calls.json",
  "sales/sales-contacts.json",
  "signals/get-signals.json",
  "signal-alarms/active-all.json",
  "signal-alarms/stats.json",
  "signal-alarm-rules/get-rules.json",
  "admin-logs/logs-for-admin.json",
  "companies/get-shipment-reports-for-company.json",
  "companies/get-booked-shipments-for-company.json",
  "customer-invoice/view-customer-invoices.json",
  "customer-invoice/get-mismatched-customer-invoices.json",
  "shipments/get-notes-for-shipments.json",
  "stats/get-menubar-stats.json",
  "shipments/shipments-need-insurance.json",
  // Amy previews panel — synthetic fixtures (preview slots and worktree sizes
  // are ephemeral infra state with no stable production export; see
  // public/fixtures-synthetic/).
  "agent-host/previews.json",
  "agent-host/worktrees.json",
  // People module — synthetic fixtures (see public/fixtures-synthetic/). The
  // browser fetch tries /fixtures first, then /fixtures-synthetic.
  "people/me.json",
  "people/me/profile.json",
  "people/recruiting/list.json",
  "people/recruiting/get.json",
  "people/recruiting/resume-data.json",
  "people/team/list.json",
  "people/team/get.json",
  "people/teams/list.json",
  "people/teams/get.json",
  "people/software/list.json",
  "people/payroll/list.json",
  "people/payroll/get.json",
]);
